import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { VariableSchemaFormComponent } from '../variable-schema-form/variable-schema-form.component';

export interface SchemaValuesDialogData {
  /** JSON Schema (variable_schema) describing the operator inputs. */
  schema: any;
  /** Optional values to prefill the form. */
  initialValues?: Record<string, unknown>;
  /** Dialog title (defaults to "Run all steps"). */
  title?: string;
  /** Confirm button label (defaults to "Run"). */
  confirmLabel?: string;
}

export interface SchemaValuesDialogResult {
  /** The validated, collected form values. */
  values: Record<string, unknown>;
}

/**
 * Generic dialog that renders a variable_schema as a form and returns the
 * collected values. Unlike RunNowDialogComponent it is routine-agnostic — it
 * only collects and returns values, so it can drive any schema-based input
 * flow (SCRUM-690).
 */
@Component({
  selector: 'app-schema-values-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    VariableSchemaFormComponent,
  ],
  templateUrl: './schema-values-dialog.component.html',
  styleUrls: ['./schema-values-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchemaValuesDialogComponent {
  private dialogRef = inject(MatDialogRef<SchemaValuesDialogComponent>);
  data = inject<SchemaValuesDialogData>(MAT_DIALOG_DATA);

  readonly title = this.data.title ?? 'Run all steps';
  readonly confirmLabel = this.data.confirmLabel ?? 'Run';

  /** Whether the schema form is currently valid. Updated by the form's output. */
  schemaValid = false;
  /** Latest collected values emitted by the form. */
  schemaValues: Record<string, unknown> = {};

  onSchemaValuesChange(values: Record<string, unknown>): void {
    this.schemaValues = values;
  }

  onSchemaValidChange(valid: boolean): void {
    this.schemaValid = valid;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onRun(): void {
    this.dialogRef.close({ values: this.schemaValues });
  }
}
